import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiArrowLeft as _FiArrowLeft, FiMoreHorizontal as _FiMoreHorizontal } from "react-icons/fi";
import { FiMail, FiSmartphone, FiCheck, FiAlertTriangle } from "react-icons/fi";
import Sidebar from "../Partials/Sidebar";
import Icon from "../Partials/Icon";
import {
  fetchContractsPaginated,
  fetchEnvelopeAudit,
  resendReminder,
  voidEnvelope,
  deleteEnvelope,
} from "../../api/envelope";
import type { ContractSummary, AuditEvent } from "../../api/envelope";
import { api } from "../../api";
import { fetchContract } from "../../Context/Contract";
import { saveDocAsTemplate } from "../../Context/Template";
import SaveTemplateModal from "../QuickDoc/SaveTemplateModal";
import "../../Styles/DashboardPageStyles/Dashboard.css";
import "../../Styles/Pages/DocumentStatus.css";

const AUDIT_LABELS: Record<string, string> = {
  ENVELOPE_CREATED: "Document created",
  ENVELOPE_SENT: "Document sent",
  RECIPIENT_OPENED: "Opened",
  FIELD_UPDATED: "Filled in a field",
  SIGNATURE_APPLIED: "Signed",
  RECIPIENT_COMPLETED: "Finished signing",
  PDF_FINALIZED: "Final PDF created",
  ENVELOPE_VOIDED: "Voided",
  REMINDER_SENT: "Reminder sent",
  ENVELOPE_DOWNLOADED: "Downloaded",
  ENVELOPE_COMPLETED: "Completed by everyone",
};

const auditLabel = (type?: string) =>
  (type && AUDIT_LABELS[type]) ||
  (type ? type.charAt(0) + type.slice(1).toLowerCase().replace(/_/g, " ") : "Event");

const FiArrowLeft = _FiArrowLeft as unknown as React.FC<React.SVGProps<SVGSVGElement> & { size?: number }>;
const FiMoreHorizontal = _FiMoreHorizontal as unknown as React.FC<React.SVGProps<SVGSVGElement> & { size?: number }>;

function DocumentStatusDetailPage() {
  const { contractURL } = useParams<{ contractURL: string }>();
  const navigate = useNavigate();

  // Contract data
  const [contract, setContract] = useState<ContractSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Audit events
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  // Action states
  const [voidLoading, setVoidLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [remindingEmail, setRemindingEmail] = useState<string | null>(null);

  // Save as template states
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateDoc, setTemplateDoc] = useState<any[]>([]);

  // Track initial load vs background poll
  const initialLoadDone = useRef(false);

  // Reusable fetch for contract data (used on initial load and polling)
  const fetchEnvelopeDetail = useCallback(async () => {
    if (!contractURL) return;
    try {
      // Only show loading spinner on initial load, not on polls
      if (!initialLoadDone.current) {
        setLoading(true);
        setError(null);
      }
      const res = await fetchContractsPaginated({
        search: contractURL,
        limit: 50,
      });
      const found = res.contracts.find(
        (c) => c.contractURL === contractURL
      );
      if (found) {
        setContract(found);
      } else if (!initialLoadDone.current) {
        setError("Document not found.");
      }
    } catch (err: any) {
      console.error("FetchContractDetailFailed", err);
      if (!initialLoadDone.current) {
        setError("Could not load document details.");
      }
    } finally {
      if (!initialLoadDone.current) {
        setLoading(false);
        initialLoadDone.current = true;
      }
    }
  }, [contractURL]);

  // Reusable fetch for audit events
  const fetchAuditData = useCallback(async () => {
    if (!contractURL) return;
    try {
      if (!initialLoadDone.current) {
        setAuditLoading(true);
        setAuditError(null);
      }
      const res = await fetchEnvelopeAudit(contractURL);
      setAuditEvents(Array.isArray(res?.events) ? res.events : []);
    } catch (err: any) {
      console.error("FetchAuditFailed", err);
      if (!initialLoadDone.current) {
        setAuditError("Could not load activity timeline.");
      }
    } finally {
      // Always clear: the detail fetch may have flipped initialLoadDone first
      setAuditLoading(false);
    }
  }, [contractURL]);

  // Initial fetch
  useEffect(() => {
    fetchEnvelopeDetail();
  }, [fetchEnvelopeDetail]);

  useEffect(() => {
    fetchAuditData();
  }, [fetchAuditData]);

  // Poll for real-time updates every 30 seconds
  useEffect(() => {
    if (!contractURL) return;
    const interval = setInterval(() => {
      fetchEnvelopeDetail();
      fetchAuditData();
    }, 30000);
    return () => clearInterval(interval);
  }, [contractURL, fetchEnvelopeDetail, fetchAuditData]);

  const sortedAudit = useMemo(() => {
    const list = auditEvents.slice();
    list.sort((a, b) => {
      const ta = a?.occurredAt ? Date.parse(a.occurredAt) : 0;
      const tb = b?.occurredAt ? Date.parse(b.occurredAt) : 0;
      return tb - ta;
    });
    return list;
  }, [auditEvents]);

  const handleRemind = useCallback(
    async (recipientEmail: string) => {
      if (!contractURL) return;
      try {
        setRemindingEmail(recipientEmail);
        await resendReminder(contractURL, recipientEmail);
        toast.success(`Reminder sent to ${recipientEmail}`);
      } catch (err: any) {
        console.error("RemindRecipientFailed", err);
        toast.error("Could not send reminder. Please try again.");
      } finally {
        setRemindingEmail(null);
      }
    },
    [contractURL]
  );

  const handleVoid = useCallback(async () => {
    if (!contractURL) return;
    const confirmed = window.confirm(
      "Are you sure you want to void this envelope? This action cannot be undone."
    );
    if (!confirmed) return;

    try {
      setVoidLoading(true);
      await voidEnvelope(contractURL);
      toast.success("Envelope has been voided.");
      // Refresh contract data
      const res = await fetchContractsPaginated({
        search: contractURL,
        limit: 50,
      });
      const found = res.contracts.find((c) => c.contractURL === contractURL);
      if (found) setContract(found);
    } catch (err: any) {
      console.error("VoidFailed", err);
      toast.error("Could not void envelope. Please try again.");
    } finally {
      setVoidLoading(false);
    }
  }, [contractURL]);

  const handleDelete = useCallback(async () => {
    if (!contractURL) return;
    const confirmed = window.confirm(
      "Are you sure you want to delete this document? This action cannot be undone."
    );
    if (!confirmed) return;

    try {
      setDeleteLoading(true);
      await deleteEnvelope(contractURL);
      toast.success("Document deleted");
      navigate("/document-status");
    } catch (err: any) {
      console.error("DeleteFailed", err);
      toast.error("Could not delete document. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  }, [contractURL, navigate]);

  /* ------------------- Save as Template ------------------- */

  const handleOpenSaveTemplate = useCallback(async () => {
    if (!contractURL) return;
    try {
      // Fetch the full contract detail (with layers)
      const detail = await fetchContract(contractURL);
      if (!detail) {
        toast.error("Could not load document data.");
        return;
      }

      // Reconstruct a QuickDoc doc array from bottomLayer + inputLayer
      const bottomLayer: any[] = Array.isArray(detail.contractBottomLayer)
        ? detail.contractBottomLayer
        : [];
      const inputLayer: any[] = Array.isArray(detail.contractInputLayer)
        ? detail.contractInputLayer
        : [];

      const doc: any[] = [];
      const pageCount = Math.max(bottomLayer.length, inputLayer.length);
      for (let i = 0; i < pageCount; i++) {
        const bottom = bottomLayer[i] || {};
        const input = inputLayer[i] || {};
        doc.push({
          id: bottom.id || input.id || `page-${i}`,
          bg: bottom.bg ?? null,
          elements: input.elements || [],
        });
      }

      setTemplateDoc(doc);
      setSaveTemplateOpen(true);
    } catch (err: any) {
      console.error("OpenSaveTemplateFailed", err);
      toast.error("Could not load document data.");
    }
  }, [contractURL]);

  const handleSaveTemplateConfirm = useCallback(
    async (data: {
      name: string;
      signerLabels: Record<string, string>;
      doc: any[];
    }) => {
      setSavingTemplate(true);
      try {
        const result = await saveDocAsTemplate(
          data.name,
          data.doc,
          data.signerLabels
        );
        if (result?.templateURL) {
          toast.success("Saved as template!");
          setSaveTemplateOpen(false);
        } else {
          toast.error("Failed to save template.");
        }
      } catch {
        toast.error("Failed to save template.");
      } finally {
        setSavingTemplate(false);
      }
    },
    []
  );

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "--";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getStatusPillClass = (computedStatus: string) => {
    const s = computedStatus.toLowerCase().replace(/[\s_]+/g, "-");
    switch (s) {
      case "sent":
        return "status-pill--sent";
      case "partially-signed":
        return "status-pill--partially-signed";
      case "completed":
        return "status-pill--completed";
      case "voided":
        return "status-pill--voided";
      case "expired":
        return "status-pill--expired";
      default:
        return "status-pill--sent";
    }
  };

  const isTerminal =
    contract?.computedStatus.toLowerCase() === "completed" ||
    contract?.computedStatus.toLowerCase() === "voided";

  const isCompleted = contract?.computedStatus.toLowerCase() === "completed";

  // Overflow menu state
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close overflow menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="dashboard">
      <Sidebar active="Document Status" />

      <div className="dashboard-main">
        {/* Back button */}
        <button
          className="ds-back-button"
          onClick={() => navigate("/document-status")}
        >
          <FiArrowLeft size={16} />
          Back to Document Status
        </button>

        {loading && (
          <div className="ds-loading">
            <div className="ds-loading-spinner" />
            <p>Loading document details...</p>
          </div>
        )}

        {!loading && error && (
          <div className="ds-empty-state">
            <p className="ds-empty-title">{error}</p>
            <button
              className="secondary-button"
              onClick={() => navigate("/document-status")}
            >
              Go back
            </button>
          </div>
        )}

        {!loading && !error && contract && (
          <>
            {/* Title + status */}
            <div className="ds-detail-title-row">
              <h1 className="ds-detail-title">{contract.contractName}</h1>
              <span
                className={`status-pill ${getStatusPillClass(contract.computedStatus)}`}
              >
                {contract.computedStatus}
              </span>
            </div>

            {/* Expiration display */}
            {contract.expiresAt && (() => {
              const expiresDate = new Date(contract.expiresAt);
              const now = new Date();
              const msLeft = expiresDate.getTime() - now.getTime();
              const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
              const isExpiringSoon = daysLeft <= 3 && daysLeft > 0;
              const isExpired = daysLeft <= 0;

              let label = `Expires on ${expiresDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
              if (isExpired) {
                label = "Expired";
              } else if (daysLeft === 1) {
                label = "Expires tomorrow";
              } else if (isExpiringSoon) {
                label = `Expires in ${daysLeft} days`;
              }

              return (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 700,
                    marginBottom: 16,
                    background: isExpired ? '#fef2f2' : isExpiringSoon ? '#fffbeb' : '#f0fdf4',
                    color: isExpired ? '#991b1b' : isExpiringSoon ? '#92400e' : '#166534',
                    border: `1px solid ${isExpired ? '#fecaca' : isExpiringSoon ? '#fde68a' : '#bbf7d0'}`,
                  }}
                >
                  {(isExpired || isExpiringSoon) && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  )}
                  {label}
                </div>
              );
            })()}

            {/* Recipients section */}
            <h3 className="ds-section-title">Recipients</h3>
            <div className="ds-recipient-cards">
              {contract.recipients.map((r, idx) => {
                const isViewer = (r.role || "").toLowerCase() === "viewer";
                const channels = [
                  { kind: "email" as const, value: r.email, display: r.email, primary: true } as any,
                  ...(r.contacts || []).map((c) => ({ ...c, primary: false })),
                ];
                const usedToSign = (c: any) =>
                  !!r.signedVia && r.signedVia.kind === c.kind && r.signedVia.value === c.value;
                return (
                  <div className="ds-recipient-card" key={r.email + idx}>
                    <div className="ds-recipient-card-header">
                      <div className="ds-recipient-id">
                        {r.name && r.name !== r.email && (
                          <span className="ds-recipient-name">{r.name}</span>
                        )}
                        <span className="ds-recipient-email">{r.email}</span>
                      </div>
                      <span className="ds-recipient-role">{r.role}</span>
                    </div>
                    <div className="ds-recipient-status">
                      {isViewer ? (
                        <span className="ds-recipient-opened">Receives a copy</span>
                      ) : r.signed ? (
                        <span className="ds-recipient-signed">
                          Signed {r.signedAt ? `on ${formatDate(r.signedAt)}` : ""}
                        </span>
                      ) : (
                        <span className="ds-recipient-pending">
                          Awaiting signature
                        </span>
                      )}
                      {r.openedAt && !r.signed && (
                        <span className="ds-recipient-opened">
                          Opened on {formatDate(r.openedAt)}
                        </span>
                      )}
                    </div>

                    {channels.length > 1 && (
                      <ul className="ds-channels" aria-label="Where the link was sent">
                        {channels.map((c: any, ci: number) => (
                          <li key={ci} className={"ds-channel" + (usedToSign(c) ? " is-used" : "")}>
                            <span className={"ds-channel-icon" + (c.kind === "sms" ? " is-sms" : "")}>
                              <Icon icon={c.kind === "sms" ? FiSmartphone : FiMail} />
                            </span>
                            <span className="ds-channel-value">{c.display}</span>
                            {usedToSign(c) ? (
                              <span className="ds-channel-tag is-used">
                                <Icon icon={FiCheck} /> Signed here
                              </span>
                            ) : c.deliveryError ? (
                              <span className="ds-channel-tag is-error" title={c.deliveryError}>
                                <Icon icon={FiAlertTriangle} /> Not delivered
                              </span>
                            ) : c.openedAt ? (
                              <span className="ds-channel-tag">Opened</span>
                            ) : c.primary ? (
                              <span className="ds-channel-tag is-muted">Main</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                    {channels.length === 1 && r.signedVia && r.signed && (
                      <div className="ds-recipient-opened">Signed from {r.signedVia.display}</div>
                    )}

                    {!r.signed && !isViewer && !isTerminal && (
                      <div className="ds-recipient-actions">
                        <button
                          className="link-button"
                          disabled={remindingEmail === r.email}
                          onClick={() => handleRemind(r.email)}
                        >
                          {remindingEmail === r.email
                            ? "Sending..."
                            : channels.length > 1
                            ? `Send reminder to all ${channels.length}`
                            : "Send Reminder"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Activity timeline */}
            <h3 className="ds-section-title">Activity Timeline</h3>
            {auditLoading && (
              <div className="ds-loading" style={{ padding: "20px 0" }}>
                <div className="ds-loading-spinner" />
                <p>Loading activity...</p>
              </div>
            )}
            {!auditLoading && auditError && (
              <p style={{ color: "#6b7280", fontSize: 13 }}>{auditError}</p>
            )}
            {!auditLoading && !auditError && sortedAudit.length === 0 && (
              <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 24 }}>
                No activity recorded yet.
              </p>
            )}
            {!auditLoading && !auditError && sortedAudit.length > 0 && (
              <div className="ds-timeline">
                {sortedAudit.map((ev, idx) => (
                  <div
                    className="ds-timeline-event"
                    key={(ev?.id || "") + idx}
                  >
                    <div className="ds-timeline-content">
                      <div className="ds-timeline-type">
                        {auditLabel(ev?.type)}
                        {ev?.meta?.viaValue && (
                          <span className="ds-timeline-via">
                            {" "}via {ev.meta.via === "sms" ? "text to " : ""}
                            {ev.meta.viaValue}
                          </span>
                        )}
                      </div>
                      {ev?.signerEmail && (
                        <div className="ds-timeline-email">
                          {ev.signerEmail}
                        </div>
                      )}
                      <div className="ds-timeline-meta">
                        {ev?.occurredAt
                          ? new Date(ev.occurredAt).toLocaleString()
                          : "Unknown time"}
                        {ev?.ip && ` \u00B7 IP ${ev.ip}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action bar */}
            <div className="ds-action-bar">
              <button
                className="ds-action-btn-primary"
                onClick={() => navigate(`/envelope/${contractURL}`)}
              >
                View Document
              </button>
              <a
                className="ds-action-btn-secondary"
                href={
                  isCompleted
                    ? `${api.defaults.baseURL}/api/envelope/${contractURL}/pdf?signed=true&download=true`
                    : `${api.defaults.baseURL}/api/envelope/${contractURL}/pdf?download=true`
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </a>
              <div className="ds-overflow-menu-wrapper" ref={menuRef}>
                <button
                  className="ds-overflow-trigger"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  aria-label="More actions"
                >
                  <FiMoreHorizontal size={18} />
                </button>
                {menuOpen && (
                  <div className="ds-overflow-menu">
                    <button
                      className="ds-overflow-item"
                      onClick={() => {
                        setMenuOpen(false);
                        window.open(
                          `${api.defaults.baseURL}/api/envelope/${contractURL}/pdf?download=true`,
                          "_blank"
                        );
                      }}
                    >
                      Download Original
                    </button>
                    {isCompleted && (
                      <button
                        className="ds-overflow-item"
                        onClick={() => {
                          setMenuOpen(false);
                          window.open(
                            `${api.defaults.baseURL}/api/envelope/${contractURL}/pdf?signed=true&download=true`,
                            "_blank"
                          );
                        }}
                      >
                        Download Signed
                      </button>
                    )}
                    <button
                      className="ds-overflow-item"
                      onClick={() => {
                        setMenuOpen(false);
                        handleOpenSaveTemplate();
                      }}
                    >
                      Save as Template
                    </button>
                    {!isTerminal && (
                      <button
                        className="ds-overflow-item"
                        onClick={() => {
                          setMenuOpen(false);
                          // Remind the first pending recipient
                          const pending = contract.recipients.find((r) => !r.signed);
                          if (pending) handleRemind(pending.email);
                        }}
                      >
                        Send Reminder
                      </button>
                    )}
                    <div className="ds-overflow-divider" />
                    {!isTerminal && (
                      <button
                        className="ds-overflow-item ds-overflow-item--danger"
                        disabled={voidLoading}
                        onClick={() => {
                          setMenuOpen(false);
                          handleVoid();
                        }}
                      >
                        {voidLoading ? "Voiding..." : "Void Envelope"}
                      </button>
                    )}
                    <button
                      className="ds-overflow-item ds-overflow-item--danger"
                      disabled={deleteLoading}
                      onClick={() => {
                        setMenuOpen(false);
                        handleDelete();
                      }}
                    >
                      {deleteLoading ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ------------ SAVE AS TEMPLATE MODAL ------------ */}
      <SaveTemplateModal
        isOpen={saveTemplateOpen}
        doc={templateDoc}
        defaultName={contract?.contractName || "Untitled Template"}
        onClose={() => setSaveTemplateOpen(false)}
        onSave={handleSaveTemplateConfirm}
        saving={savingTemplate}
      />
    </div>
  );
}

export default DocumentStatusDetailPage;
